/**
 * Picking the period.
 *
 * **The form submits two calendar dates and a zone. It does not submit
 * instants.** `2026-09-30` is not a moment until somebody says *where*, and a
 * browser that resolved the boundaries itself would be choosing the zone by
 * accident — its own — while the report claimed the tenant's. So the conversion
 * happens once, in `resolveAnalyticsRange`, and this screen shows the result
 * *before* anything is asked for: what you saw is what you asked for.
 *
 * **The last day is included, and the preview says what that becomes.** People
 * think "the 1st to the 30th, including the 30th"; a window tiles without gaps
 * only if its upper bound is exclusive. That inclusive-to-exclusive step is the
 * single most bug-prone line in the slice — get it wrong and the last day of
 * every period silently goes missing, which nobody notices until two months are
 * added together — so the field says *Included*, and
 * {@link AnalyticsRangeStatement} prints the real boundaries underneath in the
 * zone they were read in. The alternative, echoing back the two dates somebody
 * typed, agrees with the form and not necessarily with the report.
 *
 * **`now` ticks while this panel is open**, exactly as `RequestReportPanel`'s
 * does and for the same reason: a screen left open across midnight would
 * otherwise resolve "the 30 days up to yesterday" against yesterday's yesterday,
 * and the "this period has not finished" note would go on being shown for a
 * period that has since closed.
 *
 * **Nothing here re-implements a rule the contract owns.** A reversed range, an
 * impossible day like `2026-02-30`, and a period longer than
 * `MAX_REPORT_WINDOW_DAYS` are all refused by `analyticsRangeRequestSchema`,
 * which is the same schema the API validates against — so the message a person
 * reads here is the message the server would have sent. A second copy of those
 * rules in this file would be a second opinion about what this app may ask for,
 * and the two would disagree the first time either was edited.
 *
 * **A period that cannot be worked out is refused rather than approximated.**
 * When `Intl` will not resolve the zone, `resolveAnalyticsRange` returns `null`,
 * the button is disabled and the statement block says so. There is no UTC
 * fallback here for the reason the contract gives: a period bucketed against the
 * wrong clock produces a screen that looks exactly like a correct one.
 *
 * **This file imports no API client**, which is what lets
 * `scripts/verify-analytics-screens.tsx` render it. The fetch belongs to
 * `AnalyticsPage`; what this panel does is hand back a request it has already
 * shown the reader.
 *
 * **Slice 17 shortened the card description and changed no control.** It said
 * the boundary is the first instant after the last day; {@link
 * AnalyticsRangeStatement}, four lines below it, says the same thing against the
 * boundary itself, which is where a reader can act on it. Both fields still say
 * *Included*, the cap is still stated on the field it applies to, and the
 * preview is still rendered before anything is asked for — the three things
 * this header argues for are the three that could not be cut.
 */
import {
  MAX_REPORT_WINDOW_DAYS,
  analyticsRangeRequestSchema,
  resolveAnalyticsRange,
  type AnalyticsRange,
  type AnalyticsRangeRequest,
} from '@wend/shared';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, FormField, Input } from '@wend/ui';
import { CalendarSearch } from 'lucide-react';
import { useEffect, useState } from 'react';

import { FormAlert } from '../../components/FormAlert';
import {
  ANALYTICS_PERIOD_CHOICE_VALUES,
  ANALYTICS_PERIOD_DESCRIPTION,
  ANALYTICS_PERIOD_LABEL,
  analyticsDatesForChoice,
  rangeEndsInFuture,
  type AnalyticsPeriodChoice,
} from '../../lib/analytics-display';
import { ROOT_FIELD } from '../../lib/api/errors';
import { validate, type FormErrors } from '../../lib/form';
import { AnalyticsOpenPeriodNote, AnalyticsRangeStatement } from './AnalyticsCopy';

/** How often the clock behind the presets is refreshed. See the header. */
const NOW_TICK_MS = 60_000;

/**
 * Every complaint no field on this form is showing.
 *
 * The two date inputs render their own errors. Anything else the contract
 * refuses — a zone it will not accept, most obviously — would otherwise be
 * dropped silently, and a button that refuses without saying why is
 * indistinguishable from a button that is broken.
 */
function unattachedErrors(errors: FormErrors): string[] {
  const shown = new Set([ROOT_FIELD, 'fromDate', 'toDate']);
  return Object.entries(errors)
    .filter(([key]) => !shown.has(key))
    .map(([, message]) => message);
}

export interface AnalyticsRangePickerProps {
  /** The zone both dates are read in — named even when it was a fallback. */
  timezone: string;
  /** False when `Intl` would not name this browser's zone. */
  zoneResolved: boolean;
  /** The period the figures on screen cover, or `null` when none could be built. */
  applied: AnalyticsRangeRequest | null;
  /** True while the report for a newly chosen period is being read. */
  pending: boolean;
  onApply: (request: AnalyticsRangeRequest) => void;
}

export function AnalyticsRangePicker({
  timezone,
  zoneResolved,
  applied,
  pending,
  onApply,
}: AnalyticsRangePickerProps) {
  const [choice, setChoice] = useState<AnalyticsPeriodChoice>(
    applied === null ? 'custom' : 'last_30_days',
  );
  const [fromDate, setFromDate] = useState(applied?.fromDate ?? '');
  const [toDate, setToDate] = useState(applied?.toDate ?? '');
  const [now, setNow] = useState(() => new Date());
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(new Date());
    }, NOW_TICK_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  /*
   * The period as it stands, resolved the way the API will resolve it.
   *
   * Parsed first and resolved second, both with the contract's own functions: a
   * half-typed date in a field is not an error the reader needs shouted at them,
   * it is simply not yet a period, and the statement block below says so in one
   * sentence until it is.
   */
  const candidate = analyticsRangeRequestSchema.safeParse({ fromDate, toDate, timezone });
  const range: AnalyticsRange | null = candidate.success
    ? resolveAnalyticsRange(candidate.data)
    : null;

  function selectChoice(next: AnalyticsPeriodChoice): void {
    setChoice(next);
    setErrors({});

    if (next === 'custom') return;

    // Resolved against the clock at the moment it is chosen, not the moment the
    // screen was opened.
    const stamp = new Date();
    setNow(stamp);

    const dates = analyticsDatesForChoice(next, stamp, timezone, { fromDate, toDate });
    if (dates === null) {
      setErrors({
        [ROOT_FIELD]: `Wend could not work out that period in ${timezone}, so it has filled nothing in rather than reading the wrong days.`,
      });
      return;
    }
    setFromDate(dates.fromDate);
    setToDate(dates.toDate);
  }

  function onSubmit(): void {
    if (pending) return;

    const result = validate(analyticsRangeRequestSchema, { fromDate, toDate, timezone });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    const resolved = resolveAnalyticsRange(result.data);
    if (resolved === null) {
      setErrors({
        [ROOT_FIELD]: `Wend could not turn those dates into a period in ${timezone}. Nothing has been read against a guessed clock.`,
      });
      return;
    }

    setErrors({});
    onApply(result.data);
  }

  const complaints = unattachedErrors(errors);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pick a period</CardTitle>
        <CardDescription>
          Both dates are included, and both are read in {timezone}. What gets read is the period
          printed under the fields, not the two dates on their own.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex max-w-2xl flex-col gap-5">
        {errors[ROOT_FIELD] !== undefined && (
          <FormAlert tone="error">{errors[ROOT_FIELD]}</FormAlert>
        )}

        {complaints.length > 0 && <FormAlert tone="error">{complaints.join(' ')}</FormAlert>}

        <fieldset className="flex flex-col gap-2.5" disabled={pending}>
          <legend className="mb-1.5 text-[0.85rem] font-medium text-text">Period</legend>

          {ANALYTICS_PERIOD_CHOICE_VALUES.map((value) => (
            <PeriodChoice
              key={value}
              checked={choice === value}
              label={ANALYTICS_PERIOD_LABEL[value]}
              description={ANALYTICS_PERIOD_DESCRIPTION[value]}
              onSelect={() => {
                selectChoice(value);
              }}
            />
          ))}
        </fieldset>

        <div className="flex flex-wrap gap-4">
          <FormField
            label="First day"
            description="Included."
            error={errors.fromDate}
            className="min-w-[12rem] flex-1"
          >
            <Input
              type="date"
              value={fromDate}
              disabled={pending}
              onChange={(event) => {
                setFromDate(event.target.value);
                setChoice('custom');
                setErrors({});
              }}
            />
          </FormField>

          <FormField
            label="Last day"
            description={`Included. At most ${String(MAX_REPORT_WINDOW_DAYS)} days in one period.`}
            error={errors.toDate}
            className="min-w-[12rem] flex-1"
          >
            <Input
              type="date"
              value={toDate}
              disabled={pending}
              onChange={(event) => {
                setToDate(event.target.value);
                setChoice('custom');
                setErrors({});
              }}
            />
          </FormField>
        </div>

        {/*
          SLICE 30: THE PREVIEW DRAWS ONLY WHILE THERE IS NOTHING TO PREVIEW
          AGAINST.

          `AnalyticsRangeStatement` is about 65 words and `AnalyticsOpenPeriodNote`
          about 26, and both were rendered here AND again in `AnalyticsReportView`
          on every page load that had a report — same words, and in the ordinary
          case the same dates, twice on one screen.

          The page already knows which of the two is authoritative: its own
          comment beside the report says the period is *"the response's, never the
          form's"*. So the form's copy is a preview, and a preview is worth
          showing only while nobody has the real thing yet.

          `applied === null` is exactly that condition, and it is already a prop.
          Nothing is lost in either state: before a report, this is the only
          statement; after one, the report's is the true one.
        */}
        {applied === null && (
          <>
            <AnalyticsRangeStatement
              range={range}
              timezone={timezone}
              zoneResolved={zoneResolved}
              heading="What this period covers"
            />

            {range !== null && rangeEndsInFuture(range, now) && <AnalyticsOpenPeriodNote />}
          </>
        )}

        <div>
          <Button
            type="button"
            loading={pending}
            disabled={range === null}
            leadingIcon={<CalendarSearch aria-hidden="true" className="size-4" />}
            onClick={onSubmit}
          >
            {pending ? 'Reading' : 'Show this period'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** One period option. Mirrors the report screen's control, for one habit. */
function PeriodChoice({
  checked,
  onSelect,
  label,
  description,
}: {
  checked: boolean;
  onSelect: () => void;
  label: string;
  description: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-base border border-line bg-white/[0.02] px-3.5 py-3 hover:border-line-strong">
      <input
        type="radio"
        name="analytics-period"
        className="mt-1 size-4 shrink-0 accent-brand-b"
        checked={checked}
        onChange={onSelect}
      />
      <span className="min-w-0">
        <span className="block text-[0.9rem] text-text">{label}</span>
        <span className="mt-0.5 block text-[0.82rem] leading-snug text-muted">{description}</span>
      </span>
    </label>
  );
}
